import * as React from 'react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { JSX } from 'react'
import { createPortal } from 'react-dom'

import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type {
  CollisionDetection,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core'
import { Temporal } from '@js-temporal/polyfill'
import { clsx } from 'clsx'

import { DROP_ANIMATION } from '~publish/helpers/dndkit/constants'
import { restrictToRef } from '~publish/helpers/dndkit/restrictToRefModifier'
import type { WeekAnchor } from '~publish/helpers/temporal'
import { useHasElementScroll } from '~publish/hooks/useHasElementScrolled'

import { calendarKeyboardCoordinateGetter } from './calendarKeyboardCoordinateGetter'
import { CalendarContext } from './context'
import type { CalendarContextType } from './context'
import { DraggableItem } from './DraggableItem'
import { Header } from './Header'
import { calculateCalendarData, getDndKitAnnouncements } from './helpers'
import { useAnimationDirection } from './hooks/useAnimationDirection'
import { useCalendar } from './hooks/useCalendar'
import { useTimezonedNow } from './hooks/useTimezonedNow'
import { Month } from './Month'
import { ThreeDay } from './ThreeDay'
import type {
  AddItemProps,
  CalendarBlockData,
  CalendarItem,
  RenderItemFunction,
} from './types'
import { Week } from './Week'

import styles from './Calendar.module.css'

const MIN_CARD_COLUMN_WIDTH = 120
const DAYS_IN_WEEK = 7

type MaybePromise<T> = T | Promise<T>

const bucketItemsByBlocks = (
  items: CalendarItem[] | undefined,
  blocks: CalendarBlockData[][],
  isInBlock: (item: CalendarItem, block: CalendarBlockData) => boolean,
): Map<number, CalendarItem[]> | undefined => {
  if (!items) return undefined

  const sortedItems = items.toSorted((a, b) => a.timestamp - b.timestamp)
  const buckets = new Map<number, CalendarItem[]>()
  const flattenedBlocks = blocks.flat()

  for (const item of sortedItems) {
    for (const block of flattenedBlocks) {
      if (!isInBlock(item, block)) continue

      const bucket = buckets.get(block.startTimestamp)
      if (bucket) {
        bucket.push(item)
      } else {
        buckets.set(block.startTimestamp, [item])
      }
      break
    }
  }

  return buckets
}

const calendarCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args)
  return pointerCollisions.length > 0
    ? pointerCollisions
    : rectIntersection(args)
}

type CalendarProps = {
  /** Extra class name for the calendar pane container */
  className?: string
  /** Content rendered alongside the scrollable calendar inside the pane */
  calendarSiblings?: React.ReactNode
  headerProgress?: React.ReactNode
  /** Sibling content that should participate in the calendar drag context */
  children?: React.ReactNode
  /** Whether to use 24-hour format */
  is24HourFormat: boolean
  /** Whether the calendar is read-only */
  readOnly: boolean
  /** Array of calendar items */
  items: CalendarItem[] | undefined
  /** Timezone string */
  timezone: string
  /** Day of the week to start on (0 for Sunday, 1 for Monday) */
  weekStartsOn: 0 | 1
  weekAnchor?: WeekAnchor
  /** Function to render individual calendar items */
  renderItem: RenderItemFunction
  /** Props to be added on the new item button */
  addItemProps: AddItemProps
  /** Callback function for when drag starts */
  onDragStart: (event: DragStartEvent) => void
  /** Callback function for when drag ends */
  onDragEnd: (result: DragEndEvent) => MaybePromise<void>
  /** Whether to keep the overlay visible until drag-end work completes */
  shouldWaitForDragEnd?: (result: DragEndEvent) => boolean
  /** Element that bounds drag movement */
  dragBoundaryRef?: React.RefObject<HTMLElement>
  /** Render overlays for draggable items that are not calendar items */
  renderDragOverlay?: (active: DragStartEvent['active']) => React.ReactNode
  /** Whether the calendar is loading */
  loading: boolean
  /** Whether the account preferences that shape the grid chrome are unknown */
  preferencesLoading?: boolean
  scrollMonthToToday?: boolean
  scrollToTime?: number
}

/**
 * Calendar component that displays events in a week or month view.
 *
 * @param {CalendarProps} props - The props for the Calendar component
 * @returns {JSX.Element} The rendered Calendar component
 */
export const Calendar = React.memo(
  ({
    className,
    calendarSiblings,
    headerProgress,
    children,
    is24HourFormat,
    readOnly,
    items,
    loading,
    preferencesLoading = false,
    scrollMonthToToday = false,
    scrollToTime,
    timezone,
    weekStartsOn,
    weekAnchor = 'week-start',
    renderItem,
    addItemProps: addItem,
    onDragStart,
    onDragEnd,
    shouldWaitForDragEnd,
    dragBoundaryRef,
    renderDragOverlay,
  }: CalendarProps): JSX.Element => {
    const [hasLoaded, setHasLoaded] = useState(!loading && !preferencesLoading)
    useLayoutEffect(() => {
      if (!loading && !preferencesLoading) setHasLoaded(true)
    }, [loading, preferencesLoading])
    const hoursLoading = preferencesLoading || !hasLoaded || !items
    const currentTime = useTimezonedNow(timezone)
    const { startDate, selectedDate, viewMode, isMobile } = useCalendar({
      weekStartsOn,
      timezone,
      weekAnchor,
    })
    const { showRightAnimation, showLeftAnimation, handleAnimationEnd } =
      useAnimationDirection(selectedDate)
    const { scrollingElementRef, hasScroll: hasScrolled } =
      useHasElementScroll<HTMLDivElement>()
    const [activeDrag, setActiveDrag] = useState<
      DragStartEvent['active'] | undefined
    >(undefined)
    const activeDragIdRef = useRef<DragStartEvent['active']['id'] | undefined>(
      undefined,
    )
    const [isDropAnimationDisabled, setIsDropAnimationDisabled] =
      useState(false)

    const { hours, days } = useMemo(
      () =>
        calculateCalendarData(selectedDate, viewMode, weekStartsOn, {
          weekAnchor,
        }),
      [selectedDate, viewMode, weekStartsOn, weekAnchor],
    )
    const itemsByHour = useMemo(
      () =>
        bucketItemsByBlocks(
          items,
          hours,
          (item, hour) =>
            item.timestamp >= hour.startTimestamp &&
            item.timestamp <= hour.endTimestamp,
        ),
      [items, hours],
    )
    const itemsByDay = useMemo(
      () =>
        bucketItemsByBlocks(
          items,
          days,
          (item, day) =>
            item.timestamp >= day.startTimestamp &&
            item.timestamp <= day.endTimestamp,
        ),
      [items, days],
    )
    const scrollTarget = useMemo(
      () =>
        scrollToTime === undefined
          ? undefined
          : Temporal.Instant.fromEpochMilliseconds(
              scrollToTime,
            ).toZonedDateTimeISO(timezone),
      [scrollToTime, timezone],
    )
    const numberOfDays = hours[0]?.length
    const [isCompact, setIsCompact] = useState(false)
    useEffect(() => {
      const element = scrollingElementRef.current
      if (!element || typeof ResizeObserver === 'undefined') return
      const columnCount = numberOfDays ?? DAYS_IN_WEEK
      const observer = new ResizeObserver(([entry]) => {
        setIsCompact(
          entry.contentRect.width / columnCount < MIN_CARD_COLUMN_WIDTH,
        )
      })
      observer.observe(element)
      return (): void => observer.disconnect()
    }, [numberOfDays, scrollingElementRef])
    const contextValue: CalendarContextType = useMemo(
      () => ({
        renderItem,
        items,
        itemsByHour,
        itemsByDay,
        addItemProps: addItem,
        readOnly,
        selectedDate,
        is24HourFormat,
        timezone,
        currentTime,
        isMobile,
        isCompact: !isMobile && isCompact,
        loading,
        preferencesLoading,
        scrollTarget,
      }),
      [
        renderItem,
        items,
        itemsByHour,
        itemsByDay,
        addItem,
        readOnly,
        selectedDate,
        is24HourFormat,
        timezone,
        currentTime,
        isMobile,
        isCompact,
        loading,
        preferencesLoading,
        scrollTarget,
      ],
    )

    const sensors = useSensors(
      useSensor(KeyboardSensor, {
        coordinateGetter: calendarKeyboardCoordinateGetter,
      }),
      useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
      useSensor(TouchSensor, {
        activationConstraint: { delay: 300, tolerance: 5 },
      }),
    )

    const handleDragStart = useCallback(
      (event: DragStartEvent): void => {
        const { active } = event
        if (!active.id) return
        activeDragIdRef.current = active.id
        setActiveDrag(active)
        onDragStart?.(event)
      },
      [onDragStart],
    )

    const handleDragEnd = useCallback(
      (event: DragEndEvent): void => {
        const isCalendarDrag = items?.some(
          (item) => item.id === event.active.id.toString(),
        )
        const shouldWait = !isCalendarDrag || shouldWaitForDragEnd?.(event)
        const endingActiveId = event.active.id

        if (!shouldWait) {
          activeDragIdRef.current = undefined
          setActiveDrag(undefined)
          setIsDropAnimationDisabled(false)
          void onDragEnd(event)
          return
        }

        setIsDropAnimationDisabled(true)
        void (async (): Promise<void> => {
          try {
            await onDragEnd(event)
          } finally {
            if (activeDragIdRef.current === endingActiveId) {
              activeDragIdRef.current = undefined
              setActiveDrag(undefined)
              setIsDropAnimationDisabled(false)
            }
          }
        })()
      },
      [items, onDragEnd, shouldWaitForDragEnd],
    )

    const handleDragCancel = useCallback((): void => {
      activeDragIdRef.current = undefined
      setActiveDrag(undefined)
      setIsDropAnimationDisabled(false)
    }, [])

    const activeDraggedId = activeDrag?.id.toString()
    const draggedItem = items?.find((item) => item.id === activeDraggedId)
    const dropAnimation =
      draggedItem && !isDropAnimationDisabled ? DROP_ANIMATION : null
    const dragOverlay = draggedItem ? (
      <DraggableItem
        key={draggedItem.id}
        id={draggedItem.id}
        index={0}
        onOverlay={true}
        timestamp={draggedItem.timestamp}
        type={draggedItem.type}
      >
        {renderItem(draggedItem)}
      </DraggableItem>
    ) : activeDrag ? (
      renderDragOverlay?.(activeDrag)
    ) : null

    return (
      <CalendarContext.Provider value={contextValue}>
        <DndContext
          autoScroll={false}
          sensors={sensors}
          onDragStart={handleDragStart}
          modifiers={[restrictToRef(dragBoundaryRef ?? scrollingElementRef)]}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          collisionDetection={calendarCollisionDetection}
          accessibility={{
            announcements: getDndKitAnnouncements({
              timezone,
              is24HourFormat,
              viewMode,
              items,
            }),
            screenReaderInstructions: {
              draggable: `
              To pick up a item, press space or enter.
              Use the arrow keys to move the item in the calendar.
              Press space or enter again to drop the item in its new position, or press escape to cancel.
            `,
            },
          }}
        >
          <div className={className}>
            <div
              ref={scrollingElementRef}
              className={styles.calendar}
              data-loading-hours={viewMode !== 'month' && hoursLoading}
              aria-busy={
                loading || (viewMode !== 'month' && hoursLoading) || undefined
              }
            >
              <table
                className={clsx(styles.calendarTable, {
                  [styles.contentHeightTable]: viewMode !== 'month',
                  [styles.animateRight]: showRightAnimation,
                  [styles.animateLeft]: showLeftAnimation,
                })}
                onAnimationEnd={handleAnimationEnd}
              >
                <Header
                  startDate={startDate}
                  withDay={viewMode === 'week' || viewMode === 'three-day'}
                  numberOfDays={numberOfDays}
                  className={clsx({
                    [styles.headerShadow]: hasScrolled,
                  })}
                  progress={headerProgress}
                />

                <tbody>
                  {viewMode === 'month' && (
                    <Month
                      weeks={days}
                      scrollContainerRef={scrollingElementRef}
                      scrollToToday={scrollMonthToToday}
                    />
                  )}
                  {viewMode !== 'month' && hoursLoading && (
                    <tr aria-hidden="true">
                      {hours[0]?.map((hour) => (
                        <td
                          aria-hidden="true"
                          key={hour.start.toString()}
                          className={styles.loadingColumn}
                        />
                      ))}
                    </tr>
                  )}
                  {viewMode === 'three-day' && !hoursLoading && (
                    <ThreeDay
                      hours={hours}
                      scrollContainerRef={scrollingElementRef}
                    />
                  )}
                  {viewMode === 'week' && !hoursLoading && (
                    <Week
                      hours={hours}
                      scrollContainerRef={scrollingElementRef}
                    />
                  )}
                </tbody>
              </table>
            </div>
            {calendarSiblings}
          </div>
          {children}
          {createPortal(
            <DragOverlay
              dropAnimation={dropAnimation}
              className={styles.dragOverlay}
            >
              {dragOverlay}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>
      </CalendarContext.Provider>
    )
  },
)

Calendar.displayName = 'Calendar'
